import { filters, getDimension, groupsForTab, tabs } from './filter.config.svelte'
import {
	FilterId,
	type FilterDimension,
	type FilterDimensionSummary,
	type FilterSummary,
	type FilterTab
} from './filter.model'

/**
 * Reactive state container for the map filter panel.
 *
 * Initial values are sourced from `filter.config.svelte.ts` — to change a
 * default, edit the dimension's `defaultValue` (boolean) or `defaultOn`
 * (multi option) there, not here.
 *
 * Field names mirror the BE persistence shape (`FilterProfileConfig` in
 * `filter.controller.svelte.ts`). The registry's `id`s are the same strings,
 * so persistence and the renderer can walk the registry and address the
 * matching field by name.
 */
class FilterState {
	// Multi-value dimensions — Record<value, boolean>. Keys are BE persistence keys.
	lotTypes: { [k: string]: boolean } = $state(this.#buildInitialMultiState(FilterId.LOT_TYPES))
	lotStates: { [k: string]: boolean } = $state(this.#buildInitialMultiState(FilterId.LOT_STATES))
	/**
	 * HINT: starts empty by design — the popTypes options are seeded at runtime by
	 * `getLocationTypes()` (BE fetch), so there is nothing to enumerate at
	 * construction. Do NOT replace this with `#buildInitialMultiState(POP_TYPES)`
	 * like its siblings: that calls the popTypes `options()` thunk, which reads the
	 * `availableTypes` store across the filter.config → availableTypes import cycle
	 * and throws at module load (order-dependent TDZ). Regression-guarded by
	 * filter.importCycle.test.ts.
	 * Slopped reference: https://dev.to/ofri-peretz/what-are-circular-dependencies-in-javascript-and-why-they-break-things-51jd
	 * For sepcs / technical details see {@linkcode filter.importCycle.test.ts}.
	 */
	popTypes: { [k: string]: boolean } = $state({})
	popMaintenance: { [k: string]: boolean } = $state(
		this.#buildInitialMultiState(FilterId.POP_MAINTENANCE)
	)

	// Booleans (BE persistence keys). `popVisibility` is the one nested shape —
	// BE persists it as `{ external, internal }`, and the registry exposes the
	// two halves as separate boolean dimensions `popVisibility.external` /
	// `popVisibility.internal`.
	popVisibility: { internal: boolean; external: boolean } = $state({
		external: this.#buildInitialBoolean(FilterId.POP_VISIBILITY_EXTERNAL),
		internal: this.#buildInitialBoolean(FilterId.POP_VISIBILITY_INTERNAL)
	})
	showOwnProjects: boolean = $state(this.#buildInitialBoolean(FilterId.SHOW_OWN_PROJECTS))
	showPublished: boolean = $state(this.#buildInitialBoolean(FilterId.SHOW_PUBLISHED))
	showRequestedByMM: boolean = $state(this.#buildInitialBoolean(FilterId.SHOW_REQUESTED_BY_MM))
	groupLots: boolean = $state(this.#buildInitialBoolean(FilterId.GROUP_LOTS))

	// UI dirty flags — not persisted. Flipped by the renderer's event handlers
	// (project rule forbids writing reactive state from `$effect`, so per-event
	// writes via `markModified()` in the renderer fill that gap).
	lotFiltersModified: boolean = $state(false)
	popFiltersModified: boolean = $state(false)

	// Matching-results table toggles (top-5-matches feature). These drive the
	// bespoke `MatchingFilter.svelte` panel shown only while `appState.isMatching`.
	// They are NOT part of the pop/lot registry: not persisted, not summary-counted.
	hideSubstitutes: boolean = $state(false)
	hideRecommendations: boolean = $state(false)
	hidePrices: boolean = $state(false)

	/**
	 * Set every option of a multi-value dimension to `value`. Options marked
	 * `disabled` (e.g. COMPLETED / DISCARDED lot states) are skipped — the
	 * "All" aggregate must not silently flip a checkbox the user can't
	 * individually toggle.
	 */
	setAll(dimensionId: string, value: boolean) {
		const dim = getDimension(dimensionId)
		if (!dim || dim.kind !== 'multi') return
		const state = this.getMultiState(dimensionId)
		if (!state) return
		for (const opt of dim.options()) {
			if (opt.disabled) continue
			state[opt.value] = value
		}
	}

	/** Set every option in a named subset of a multi-value dimension to `value`. */
	setSubset(dimensionId: string, subsetKey: string, value: boolean) {
		const dim = getDimension(dimensionId)
		if (!dim || dim.kind !== 'multi') return
		const subsetKeys = dim.subsets?.[subsetKey]?.()
		if (!subsetKeys) return
		const state = this.getMultiState(dimensionId)
		if (!state) return
		// only write keys that are actual options of the dimension (mirrors setAll),
		// so a subset listing a hidden state can never inject a phantom key
		const validValues = new Set(dim.options().map((opt) => opt.value))
		for (const k of subsetKeys) {
			if (validValues.has(k)) state[k] = value
		}
	}

	/**
	 * resolve a multi-value dimension id to the actual `$state` record.
	 *
	 * dynamic field lookup keeps the registry id and the class field name as
	 * a single source of truth — adding a new multi dimension only requires a
	 * matching `FilterId.X` constant and a same-named class field above.
	 * callers gate on `dim.kind === 'multi'` before calling, so boolean
	 * fields can never be returned by accident.
	 */
	getMultiState(id: string): Record<string, boolean> | null {
		const field = (this as unknown as Record<string, unknown>)[id]
		if (field && typeof field === 'object' && !Array.isArray(field)) {
			return field as Record<string, boolean>
		}
		return null
	}

	// ---------------------------------------------------------------------------
	// Builder helpers — read defaults from the registry at construction time.
	// Keeping these private to the module: the registry stays the public surface
	// for "what dimensions exist and what their defaults are".
	// ---------------------------------------------------------------------------

	/**
	 * Build the initial `Record<value, boolean>` for a multi-value dimension by
	 * reading its option list at construction time.
	 *
	 * Only for dimensions whose options are known synchronously (enum-sourced:
	 * `lotTypes`, `lotStates`, `popMaintenance`). `popTypes` is BE-seeded at
	 * runtime and is initialised directly as `{}` at its field above — it must
	 * NOT be routed through here (see the HINT on that field).
	 */
	#buildInitialMultiState(dimensionId: string): Record<string, boolean> {
		const dim = getDimension(dimensionId)
		if (!dim || dim.kind !== 'multi') {
			console.error(`Unknown multi dimension: ${dimensionId}`)
			throw new Error(`Unknown multi dimension: ${dimensionId}`)
		}
		const initial: Record<string, boolean> = {}
		for (const opt of dim.options()) {
			initial[opt.value] = opt.defaultOn ?? true
		}
		return initial
	}

	/** Build the initial boolean for a boolean dimension. */
	#buildInitialBoolean(dimensionId: string): boolean {
		const dim = getDimension(dimensionId)
		if (!dim || dim.kind !== 'boolean') {
			console.error(`Unknown boolean dimension: ${dimensionId}`)
			throw new Error(`Unknown boolean dimension: ${dimensionId}`)
		}
		return dim.defaultValue()
	}
}

export const filterState = new FilterState()

/**
 * reactive selected-of-total tally over `filterState`, per visible
 * user-controllable dimension. counts are registry-driven:
 *   - boolean → total 1, selected 0|1
 *   - multi   → total = options.length, selected = options whose state is true
 *
 * "All" / "All <subset>" toggles are derived affordances over the same leaf
 * options and are NOT counted (a 12-option `lotStates` always totals 12). a
 * dimension is skipped when `visible()` is false, `readOnly()` is true, or
 * `affectsResults === false` (display-only, e.g. `groupLots`).
 *
 * two aggregates are exposed: dimension-level
 * (`visibleDimensions` / `restrictedDimensions`, per checkbox-row) and
 * group-level (`visibleGroups` / `restrictedGroups`, per section — a group is
 * restricted if any of its surviving dimensions is).
 */
function tallyDimension(dim: FilterDimension): { selected: number; total: number } {
	if (dim.kind === 'boolean') {
		// Boolean ids may be dotted (e.g. `popVisibility.external`).
		const value = dim.id
			.split('.')
			.reduce<unknown>(
				(cur, part) =>
					cur != null && typeof cur === 'object'
						? (cur as Record<string, unknown>)[part]
						: undefined,
				filterState
			)
		return { selected: value ? 1 : 0, total: 1 }
	}
	const state = filterState.getMultiState(dim.id)
	const opts = dim.options()
	const selected = opts.reduce((n, o) => n + (state?.[o.value] ? 1 : 0), 0)
	return { selected, total: opts.length }
}

function buildSummary(tab: FilterTab | 'all'): FilterSummary {
	const dimensions = filters
		.filter(
			(d) =>
				(d.visible?.() ?? true) &&
				!(d.readOnly?.() ?? false) &&
				// `affectsResults` defaults to true — only an explicit `false` opts out.
				d.affectsResults !== false
		)
		.filter((d) => tab === 'all' || d.tab === tab)
		.map<FilterDimensionSummary>((d) => {
			const { selected, total } = tallyDimension(d)
			return {
				id: d.id,
				group: d.group,
				labelKey: d.labelKey(),
				selected,
				total,
				isRestricted: selected < total
			}
		})

	// Group-level aggregate. A group is counted only if (a) its own
	// `visible()` passes and (b) at least one of its dimensions survived the
	// dimension-level filters above (so a group whose every dimension is
	// readOnly / affectsResults:false drops out cleanly).
	const surviving = new Set(dimensions.map((d) => d.group))
	const restrictedGroupKeys = new Set(dimensions.filter((d) => d.isRestricted).map((d) => d.group))
	const consideredGroups = (tab === 'all' ? tabs.map((t) => t.id) : [tab])
		.flatMap((t) => groupsForTab(t))
		.filter((g) => (g.visible?.() ?? true) && surviving.has(g.key))

	return {
		selected: dimensions.reduce((s, d) => s + d.selected, 0),
		total: dimensions.reduce((s, d) => s + d.total, 0),
		restrictedDimensions: dimensions.filter((d) => d.isRestricted).length,
		visibleDimensions: dimensions.length,
		restrictedGroups: consideredGroups.filter((g) => restrictedGroupKeys.has(g.key)).length,
		visibleGroups: consideredGroups.length,
		dimensions
	}
}

class FilterSummaryStore {
	/** Pop-tab filters visible to the current user. */
	pop: FilterSummary = $derived(buildSummary('pop'))
	/** Lot-tab filters visible to the current user. */
	lot: FilterSummary = $derived(buildSummary('lot'))
	/** All visible filters across both tabs. */
	all: FilterSummary = $derived(buildSummary('all'))
}

export const filterSummary = new FilterSummaryStore()
